/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { PMSError, ValidationError } from '@/errors';
import StorageService from '@/services/storage.service';

const endpointSystems = `${apiEndpoint}/connectivity/systems`;
const endpointApaleo = `${apiEndpoint}/connectivity/apaleo`;
const endpointHostAway = `${apiEndpoint}/connectivity/hostaway`;
const endpointHostICal = `${apiEndpoint}/connectivity/i-cal`;

const initialProperty = StorageService.getProperty();

const state = {
  pending: false,
  settingPending: false,
  updatePending: false,
  settingsLoaded: false,
  logsLoaded: true,
  data: null,
  error: null,
  settingData: null,
  ccPmsData: null,
  settingError: null,
  bookingLogsData: null,
  bookingLogsStatuses: null,
  updateError: null,
  validationError: null,
  booking: null,
  updateServiceData: null,
  apaleoPropertyKey: '',
  connected: 'no',
  authUrl: '',
  clientSecretValid: '',
  syncLogs: [],
  syncLogsLoading: false,
  importLogs: [],
  syncImportLogsLoading: false,
  iCalLoading: false,
  iCalUpdating: false,
  iCalLinks: null,
};

const getters = {
  loaded: (state) => !state.pending && state.data != null,
  settingLoaded: (state) => !state.settingPending && state.settingData?.Features != null,
  systems: (state, getters) => getters.loaded && state.data,
  settings: (state, getters) => getters.settingLoaded && state.settingData,
  validationError: (state) => state.validationError,
  connection: (state) => state.connected,
  syncLogs: (state) => state.syncLogs,
  syncLogsLoading: (state) => state.syncLogsLoading,
  syncImportLogsLoading: (state) => state.syncImportLogsLoading,
  importLogs: (state) => state.importLogs,
  iCalLoading: (state) => state.iCalLoading,
  iCalUpdating: (state) => state.iCalUpdating,
  iCalLinks: (state) => state.iCalLinks,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
    state.validationError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.data = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeSettingLoading(state, initial = false) {
    if (initial) {
      state.settingData = null;
    }
    state.settingsLoaded = false;
    state.settingError = null;
    state.settingPending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
    state.validationError = null;
  },
  beforeLogsUpdate(state) {
    state.logsLoaded = false;
  },
  beforeSyncLogs(state) {
    state.syncLogsLoading = true;
  },
  afterSyncLogs(state, data) {
    state.syncLogsLoading = false;
    state.syncLogs = data;
  },
  beforeImportLogs(state) {
    state.syncImportLogsLoading = true;
  },
  afterImportLogs(state, data) {
    state.syncImportLogsLoading = false;
    state.importLogs = data;
  },
  setImportLogs(state, data) {
    state.importLogs = data;
  },
  iCalLoading(state, data) {
    state.iCalLoading = data;
  },
  iCalUpdating(state, data) {
    state.iCalUpdating = data;
  },
  iCalLinks(state, data) {
    state.iCalLinks = data;
    state.iCalLoading = false;
  },
  afterUpdate(state) {
    state.updatePending = false;
    state.logsLoaded = true;
  },
  data(state, { data = null, error = null } = {}) {
    state.pending = false;
    state.data = data;
    state.error = error;
  },
  settingData(state, { data = null, error = null } = {}) {
    state.settingPending = false;
    state.settingsLoaded = true;
    state.settingData = data;
    state.settingError = error;
  },
  ccPmsData(state, { data = null, error = null } = {}) {
    state.settingPending = false;
    state.settingsLoaded = true;
    state.ccPmsData = data?.data?.pms_details ?? null;
    state.settingError = error;
  },
  bookingData(state, { data = null }) {
    state.booking = (data.status === 200 && data.data) ? data.data : null;
    state.updatePending = false;
  },
  updateDataUpdateServiceTable(state, { data = null }) {
    state.updateServiceData = (data.status === 200 && data.data) ? data.data : [];
    state.updatePending = false;
  },
  updateBookingLogs(state, { data = null }) {
    state.bookingLogsData = data.data?.data ?? [];
    state.bookingLogsStatuses = data.data?.queue_statuses ?? {};
    state.logsLoaded = true;
  },
  modified(state, { system = null, software, error = null } = {}) {
    const o = state.data.find((p) => p.active != null);
    const same = o != null && o.id === system;
    if (o != null && !same) {
      Vue.delete(o, 'active');
    }
    if (system != null) {
      const n = same ? o : state.data.find((p) => p.id === system);
      if (n != null) {
        Vue.set(n, 'active', software);
      }
    }
    state.updatePending = false;
    state.updateError = error;
  },
  validationError(state, error) {
    state.validationError = error;
    state.pending = false;
  },
  setapaleoPropertyKey(state, newValue) {
    state.apaleoPropertyKey = newValue;
  },
  setStatus(state, { status }) {
    state.connected = status;
  },
  setAuthUrl(state, { data }) {
    state.authUrl = data;
  },
  setClientSecretValid(state, { valid }) {
    state.clientSecretValid = valid;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 409:
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }

  return err;
}

const actions = {
  async fetchData({ commit, state }, forced = false) {
    if (!forced && state.data != null) return;
    commit('beforeLoading');
    try {
      const { data } = await ApiService.get(`${endpointSystems}`);
      commit('data', { data });
    } catch (error) {
      commit('data', { error });
      handleApiError(commit, error);
    }
  },
  async getCcPmsList({ commit, state }, forced = false) {
    if (!forced && state.data != null) return;
    commit('beforeLoading');
    try {
      const { data } = await ApiService.get(`${endpointSystems}/cc-pms-list`);
      commit('ccPmsData', { data });
    } catch (error) {
      commit('ccPmsData', { error });
    }
  },
  async getSettingsData({ commit, state }, forced = false) {
    if (!forced && state.settingData != null) return;
    commit('beforeSettingLoading');
    try {
      const { data } = await ApiService.get(`${endpointSystems}/settings`);
      commit('settingData', { data });
    } catch (error) {
      commit('settingData', { error });
    }
  },
  async updateSettingsData({ commit }, payload) {
    try {
      await ApiService.post(`${endpointSystems}/settings`, payload);
    } catch (error) {
      commit('settingData', { error });
    }
  },
  async getAuthUrl({ commit }) {
    try {
      const { data } = await ApiService.post(`${endpointApaleo}/connect`, { object_id: initialProperty });
      commit('setAuthUrl', { data });
    } catch (error) {
      handleApiError(error);
    }
  },
  async systemState({ commit }, { system = null, software = null } = {}) {
    commit('beforeUpdate');
    try {
      if (system && software) {
        await ApiService.post(`${endpointSystems}`, { system, software });
      } else {
        await ApiService.post(`${endpointSystems}`);
      }
      commit('modified', { system, software });
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
  async systemBookingCreate({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpointSystems}/booking`, payload);
      commit('bookingData', { data });
      return data;
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async systemBookingUpdate({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.put(`${endpointSystems}/booking`, payload);
      commit('bookingData', { data });
      return data;
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async systemBookingUpdateNow({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpointSystems}/booking-update-now`, payload);
      commit('bookingData', { data });
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getSystemBookingUpdate({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpointSystems}/get-booking`, payload);
      commit('bookingData', { data });
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getDataUpdateServiceTable({ commit }) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.get(`${endpointSystems}/lookup-table`);
      commit('updateDataUpdateServiceTable', { data });
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getSystemBookingLogData({ commit }, payload) {
    commit('beforeLogsUpdate');
    try {
      const { data } = await ApiService.post(`${endpointSystems}/get-booking-logs`, payload);
      commit('updateBookingLogs', { data });
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getSystemSyncLogData({ commit }) {
    commit('beforeSyncLogs');
    try {
      const { data } = await ApiService.get(`${endpointSystems}/get-sync-logs`);
      commit('afterSyncLogs', data?.data);
    } catch (error) {
      commit('afterSyncLogs');
      throw handleApiError(commit, error);
    }
  },
  async getSystemImportLogData({ commit }, requestId) {
    commit('beforeImportLogs');
    try {
      const { data } = await ApiService.get(`${endpointSystems}/get-import-logs?requestId=${requestId}`);
      commit('afterImportLogs', data);
    } catch (error) {
      commit('afterImportLogs');
      throw handleApiError(commit, error);
    }
  },
  async apaleoObjectMap() {
    try {
      await ApiService.post(`${endpointApaleo}/object-map`, { apaleoPropertyKey: state.apaleoPropertyKey, object_id: initialProperty });
    } catch (error) {
      handleApiError(error);
    }
  },
  setapaleoPropertyKey({ commit }, newValue) {
    commit('setapaleoPropertyKey', newValue);
  },
  async checkConnection({ commit, dispatch }) {
    try {
      const { data } = await ApiService.post(`${endpointApaleo}/status`, { object_id: initialProperty });
      let status = '';
      if (data.status === 1) {
        status = 'yes';
      } else {
        status = 'no';
        dispatch('getAuthUrl');
      }
      commit('setStatus', { status });
    } catch (error) {
      handleApiError(error);
    }
  },
  async hostawaySetClientSecret({ commit }, hostawayAccountID) {
    try {
      commit('beforeUpdate');
      const { data } = await ApiService.post(`${endpointHostAway}/client-secret`, { object_id: initialProperty, account_id: hostawayAccountID.hostawayAccountID, api_key: hostawayAccountID.hostawayAPIKey });
      const valid = data.success;
      commit('setClientSecretValid', { valid });
      commit('afterUpdate');
    } catch (error) {
      handleApiError(error);
    }
  },
  async getICalLinks({ commit }, payload) {
    commit('iCalLoading', true);
    try {
      const queryParams = (new URLSearchParams(payload)).toString();
      const { data } = await ApiService.get(`${endpointHostICal}?${queryParams}`);
      commit('iCalLinks', data);
      return data;
    } catch (error) {
      commit('iCalLoading', false);
      throw handleApiError(commit, error);
    }
  },
  async setICalLinks({ commit }, payload) {
    commit('iCalUpdating', true);
    try {
      await ApiService.post(`${endpointHostICal}`, payload);
      commit('iCalUpdating', false);
    } catch (error) {
      commit('iCalUpdating', false);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
